// The layer between flat ops and a panel that reads like the good lenses.
//
// `groupActivity` produces ONE level: key → ops. Every X-ray a reader actually
// finds useful produces more than that, and the gap is structural rather than
// cosmetic. Read against the readings that work:
//
//   Research   host → page, each page with a fetch count, a "changed between reads"
//              flag, and the reconstructed content. Plus a WHO rail of endpoints.
//   Queries    target → entity, each query with its spec, its scanned-vs-returned
//              counts, and the RESULT rendered as a table.
//   Recall     topic → consultation, plus "whose work came back" as its own
//              projection of the same ops.
//
// Four things none of which a single-level grouping can express:
//
//   NESTING. `host → page` is two identities, and the outer one is only meaningful
//   as a container. Flattened to either level alone you get 588 rows or 12.
//
//   PROJECTION. The same ops keyed by ACTOR rather than by target. A reader enters
//   from "what did this endpoint do" as often as from "what happened to this
//   thing", and both rails are the same data.
//
//   SIGHTINGS. "fetched 2× · this page changed between reads" is a relation ACROSS
//   calls to one identity, computable only by comparing them. It is also the single
//   most informative thing on that panel, because a target that changed under an
//   agent is the case where its conclusion may not hold.
//
//   CENSUS. "71 pages across 588 hosts read by 11 endpoints · 91 fetches · 161
//   searches" is four orthogonal counts. One number cannot say whether a window is
//   broad and shallow or narrow and deep.
//
// Pure and deterministic, like everything else here: the same ops give the same
// structure, so a panel can be diffed and a test can pin it.

const tsOf = (v) => {
  if (v instanceof Date) return v.getTime();
  if (typeof v === "number") return v;
  const p = Date.parse(v);
  return Number.isNaN(p) ? null : p;
};

const str = (v) => (v == null ? "" : String(v));

/**
 * Sightings of one identity, ordered, with what changed between them.
 *
 * `compare` names the observation being tracked. Two calls to the same target that
 * returned different things is the finding — an agent whose conclusion rests on the
 * first reading may be wrong by the second. Absence of a comparison is reported as
 * `unknown`, never as `unchanged`: a target observed once has not been shown stable,
 * and an op whose output never reached the trace has not been shown to have any.
 */
function sightingsOf(ops, compare) {
  const ordered = [...ops].sort((a, b) => (tsOf(a.ts) ?? 0) - (tsOf(b.ts) ?? 0));
  const seen = ordered.length;
  if (!compare) return { seen, changed: null, observations: 0 };

  const values = ordered.map((op) => compare(op)).filter((v) => v != null && v !== "");
  const distinct = new Set(values.map(str));
  return {
    seen,
    // null means "not determinable", which is a third answer and not a soft no.
    changed: values.length < 2 ? null : distinct.size > 1,
    observations: values.length,
    first: values.length ? values[0] : null,
    latest: values.length ? values[values.length - 1] : null,
  };
}

/**
 * A container's sightings, from its items' verdicts.
 *
 * `changed` is true when any item changed, false when every determinable one held,
 * and null when none was determinable — the same three answers as one item, so a
 * container never claims more than its items support. `first`/`latest` are absent
 * on purpose: a container has no single observation to name.
 */
function rollUp(innerRows) {
  const verdicts = innerRows.map((i) => i.sightings?.changed);
  return {
    seen: innerRows.reduce((n, i) => n + (i.sightings?.seen || 0), 0),
    changed: verdicts.includes(true) ? true : (verdicts.includes(false) ? false : null),
    observations: innerRows.reduce((n, i) => n + (i.sightings?.observations || 0), 0),
    first: null,
    latest: null,
  };
}

function bucket(map, key) {
  let b = map.get(key);
  if (!b) { b = { key, ops: [], sessions: new Set(), endpoints: new Set() }; map.set(key, b); }
  return b;
}

function close(b, label) {
  const stamps = b.ops.map((o) => tsOf(o.ts)).filter((n) => n != null);
  return {
    key: b.key,
    label: label || b.key,
    ops: b.ops,
    sessions: b.sessions.size,
    endpoints: b.endpoints.size,
    firstTs: stamps.length ? Math.min(...stamps) : null,
    lastTs: stamps.length ? Math.max(...stamps) : null,
  };
}

/**
 * Flat ops → the structure a panel renders.
 *
 * @param {object[]} flatOps        from collectWindowOps
 * @param {object} spec
 *   outer(op)        REQUIRED. The container identity — a host, a target, a topic.
 *   outerLabel(op)   how it reads. Defaults to the key.
 *   inner(op)        optional. The identity WITHIN the container — a page, an
 *                    entity, a consultation. Its absence makes the structure
 *                    one-level, which is a legitimate shape, not a degraded one.
 *   innerLabel(op)
 *   actor(op)        → {id, label}. The WHO projection. Defaults to the endpoint
 *                    stamped by collectWindowOps.
 *   compare(op)      optional. The observation tracked across sightings.
 *   census           [{label, of}] where `of` is "outer"|"inner"|"ops"|"sessions"|
 *                    "endpoints" or a function (structure) => number.
 *
 * @returns {{census, actors, outer, totals}}
 */
export function buildStructure(flatOps, spec = {}) {
  const ops = Array.isArray(flatOps) ? flatOps : [];
  const { outer, outerLabel, inner, innerLabel, compare, census = [] } = spec;
  if (typeof outer !== "function") throw new TypeError("buildStructure needs outer(op)");

  const actorOf = spec.actor || ((op) => ({
    id: str(op.endpointId) || str(op.endpointName),
    label: str(op.endpointName) || str(op.endpointId) || "unattributed",
  }));

  const outerMap = new Map();
  const actorMap = new Map();
  const innerKeys = new Set();

  for (const op of ops) {
    const oKey = str(outer(op)) || "unresolved";
    const ob = bucket(outerMap, oKey);
    ob.ops.push(op);
    if (op.sessionId) ob.sessions.add(op.sessionId);
    if (op.endpointId) ob.endpoints.add(op.endpointId);
    if (!ob.label && outerLabel) ob.label = str(outerLabel(op));

    if (inner) {
      const iKey = str(inner(op)) || "unresolved";
      // Escaped, never a literal control byte in source: a raw NUL here survived
      // a paste and broke the diff tooling for this whole file. The delimiter has
      // to be a character that cannot occur in either key, or two different pairs
      // collide and `totals.inner` undercounts.
      innerKeys.add(`${oKey}\u0000${iKey}`);
      if (!ob.inner) ob.inner = new Map();
      const ib = bucket(ob.inner, iKey);
      ib.ops.push(op);
      if (op.sessionId) ib.sessions.add(op.sessionId);
      if (op.endpointId) ib.endpoints.add(op.endpointId);
      if (!ib.label && innerLabel) ib.label = str(innerLabel(op));
    }

    const a = actorOf(op) || {};
    const aKey = str(a.id) || "unattributed";
    const ab = bucket(actorMap, aKey);
    ab.ops.push(op);
    ab.label = ab.label || str(a.label) || aKey;
    if (op.sessionId) ab.sessions.add(op.sessionId);
    // Mirrored from the outer/inner buckets. Without it every actor row reported
    // `endpoints: 0`, which reads as a real measurement — and for a custom actor
    // projection (an agent, a service) the count of endpoints it ran across is the
    // interesting part, not an artifact of the default being one-to-one.
    if (op.endpointId) ab.endpoints.add(op.endpointId);
  }

  const outerRows = [...outerMap.values()].map((ob) => {
    const row = close(ob, ob.label);
    if (ob.inner) {
      row.inner = [...ob.inner.values()]
        .map((ib) => {
          const ir = close(ib, ib.label);
          ir.sightings = sightingsOf(ib.ops, compare);
          return ir;
        })
        // Busiest first WITHIN a container, then by key so the order is stable
        // across runs — a panel that reshuffles cannot be read twice.
        .sort((a, b) => b.ops.length - a.ops.length || a.key.localeCompare(b.key));
      // ROLLED UP from the items, not computed over the container's raw ops. A
      // container is not a target — its items are — so comparing observations across
      // all of them makes two stable-but-different items read as one changed thing:
      // a host serving /skills and /tools reported `changed` from that difference
      // alone, and the census counted it.
      row.sightings = rollUp(row.inner);
    } else {
      row.sightings = sightingsOf(ob.ops, compare);
    }
    return row;
  }).sort((a, b) => b.ops.length - a.ops.length || a.key.localeCompare(b.key));

  const actors = [...actorMap.values()]
    .map((ab) => close(ab, ab.label))
    .sort((a, b) => b.ops.length - a.ops.length || a.key.localeCompare(b.key));

  const totals = {
    ops: ops.length,
    outer: outerMap.size,
    inner: inner ? innerKeys.size : 0,
    sessions: new Set(ops.map((o) => o.sessionId).filter(Boolean)).size,
    endpoints: new Set(ops.map((o) => o.endpointId).filter(Boolean)).size,
    // How many TARGETS changed under the agent — the count that says whether
    // anything the session concluded may already be stale. Counted at whichever
    // level is the target: the items when there are items, the containers otherwise.
    changed: inner
      ? outerRows.reduce((n, r) => n + (r.inner || []).filter((i) => i.sightings.changed === true).length, 0)
      : outerRows.filter((r) => r.sightings.changed === true).length,
  };

  const structure = { actors, outer: outerRows, totals };
  structure.census = census
    .map(({ label, of }) => {
      const n = typeof of === "function" ? of(structure) : totals[of];
      return n == null ? null : { label, n };
    })
    .filter(Boolean);
  return structure;
}
